import { type ComponentType, lazy, type LazyExoticComponent } from 'react';

const RELOAD_KEY = 'lazyWithRetry:reloaded';
const IN_FLIGHT_RETRIES = 2;
const IN_FLIGHT_BACKOFF_MS = 250;

const isChunkLoadError = (err: unknown): boolean => {
  if (!err) {return false;}
  const message = (err as { message?: string }).message ?? String(err);
  const name = (err as { name?: string }).name ?? '';
  return (
    /Failed to fetch dynamically imported module/i.test(message) ||
    /Importing a module script failed/i.test(message) ||
    /error loading dynamically imported module/i.test(message) ||
    /Loading chunk \d+ failed/i.test(message) ||
    /ChunkLoadError/i.test(message) ||
    /ChunkLoadError/i.test(name) ||
    /^Loading CSS chunk \d+ failed/i.test(message)
  );
};

const sleep = (ms: number) => new Promise((resolve) => { setTimeout(resolve, ms); });

/**
 * Wraps React.lazy with two layers of recovery for chunk-fetch failures:
 *
 *   1) Retry the import in-flight (up to IN_FLIGHT_RETRIES with backoff)
 *      to absorb transient blips — Vite HMR restarts, network jitter, brief
 *      server unavailability. No page reload needed.
 *   2) If retries are exhausted, fall back to a single hard reload guarded
 *      by sessionStorage so we never reload-loop. After a successful import
 *      the flag is cleared.
 *
 * If everything fails post-reload, the original error propagates to the
 * route error boundary instead of leaving the user on a blank screen.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function lazyWithRetry<T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>,
): LazyExoticComponent<T> {
  return lazy(async () => {
    let lastErr: unknown;
    for (let attempt = 0; attempt <= IN_FLIGHT_RETRIES; attempt++) {
      try {
        const mod = await factory();
        if (typeof window !== 'undefined') {
          window.sessionStorage.removeItem(RELOAD_KEY);
        }
        return mod;
      } catch (err) {
        lastErr = err;
        if (!isChunkLoadError(err)) {throw err;}
        if (attempt < IN_FLIGHT_RETRIES) {
          // eslint-disable-next-line no-console
          console.warn(
            `[lazyWithRetry] dynamic import failed (attempt ${attempt + 1}/${IN_FLIGHT_RETRIES + 1}), retrying in ${IN_FLIGHT_BACKOFF_MS * (attempt + 1)}ms`,
            err,
          );
          await sleep(IN_FLIGHT_BACKOFF_MS * (attempt + 1));
        }
      }
    }

    if (typeof window === 'undefined') {throw lastErr;}
    const alreadyReloaded = window.sessionStorage.getItem(RELOAD_KEY) === '1';
    if (alreadyReloaded) {
      // eslint-disable-next-line no-console
      console.error(
        '[lazyWithRetry] dynamic import still failing after reload, surfacing to error boundary',
        lastErr,
      );
      throw lastErr;
    }
    // eslint-disable-next-line no-console
    console.warn('[lazyWithRetry] in-flight retries exhausted, hard-reloading once', lastErr);
    window.sessionStorage.setItem(RELOAD_KEY, '1');
    window.location.reload();
    // Never-resolving promise so Suspense holds the fallback through reload
    // instead of flashing the error UI.
    return new Promise<never>(() => {});
  });
}
